import type { BulkDeleteAlfredDevicesResult } from "@/types/alfred-device"

interface AlfredPageAfterBulkDeleteParams {
  page: number
  pageSize: number
  total: number
  deleted: number
}

export function getAlfredPageAfterBulkDelete({
  page,
  pageSize,
  total,
  deleted,
}: AlfredPageAfterBulkDeleteParams): number {
  const remainingDevices = Math.max(0, total - deleted)
  const lastPage = Math.max(1, Math.ceil(remainingDevices / pageSize))

  return Math.min(page, lastPage)
}

export type AlfredBulkDeletePresentation =
  | {
      kind: "success"
      variant: "success"
      title: string
      description: string
    }
  | {
      kind: "partial"
      variant: "warning"
      title: string
      description: string
    }
  | {
      kind: "failure"
      variant: "error"
      title: string
      description: string
    }

export function getAlfredBulkDeletePresentation(
  result: BulkDeleteAlfredDevicesResult,
): AlfredBulkDeletePresentation {
  if (result.failed === 0) {
    return {
      kind: "success",
      variant: "success",
      title: `${result.deleted} device${result.deleted === 1 ? "" : "s"} deleted`,
      description: "The selected devices have been permanently removed.",
    }
  }

  if (result.deleted > 0) {
    return {
      kind: "partial",
      variant: "warning",
      title: `${result.deleted} deleted, ${result.failed} failed`,
      description: "Failed devices remain selected so you can retry.",
    }
  }

  return {
    kind: "failure",
    variant: "error",
    title: "Failed to delete devices",
    description: `${result.failed} device${result.failed === 1 ? "" : "s"} failed to delete and remain selected.`,
  }
}

export function getAlfredBulkDeleteFailedEntries(
  result: BulkDeleteAlfredDevicesResult,
  submittedDevices: ReadonlyMap<number, string>,
): Array<readonly [number, string]> {
  return result.results
    .filter((item) => item.status === "failed")
    .map((item) => [
      item.id,
      submittedDevices.get(item.id) ?? "Unknown device",
    ] as const)
}
